import { httpClient } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";

export type TournamentSanction = Schema["TournamentSanctionResponse"];
export type TournamentSanctionStatus = TournamentSanction["status"];
export type TournamentSanctionPage = Schema["TournamentSanctionPage"];
export type TournamentSanctionRatingsPreview =
  Schema["TournamentSanctionRatingsPreview"];
export type TournamentSanctionDecision = Schema["TournamentSanctionDecision"];

export interface SanctionListParams {
  readonly status?: TournamentSanctionStatus;
  readonly overdue?: boolean;
  readonly q?: string;
  readonly limit: number;
  readonly offset: number;
}

const BASE = "/platform/tournament-sanctions";

export function listSanctions(
  params: SanctionListParams,
): Promise<TournamentSanctionPage> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", params.status);
  if (params.overdue) search.set("overdue", "true");
  if (params.q) search.set("q", params.q);
  search.set("limit", String(params.limit));
  search.set("offset", String(params.offset));
  return httpClient.get<TournamentSanctionPage>(`${BASE}?${search.toString()}`);
}

export function getSanction(sanctionId: string): Promise<TournamentSanction> {
  return httpClient.get<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}`,
  );
}

export function decideSanction(
  sanctionId: string,
  body: TournamentSanctionDecision,
): Promise<TournamentSanction> {
  return httpClient.post<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}/decision`,
    body,
  );
}

export function returnSanctionResults(
  sanctionId: string,
  body: Schema["TournamentSanctionReturnResults"],
): Promise<TournamentSanction> {
  return httpClient.post<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}/return-results`,
    body,
  );
}

export function previewSanctionRatings(
  sanctionId: string,
): Promise<TournamentSanctionRatingsPreview> {
  return httpClient.get<TournamentSanctionRatingsPreview>(
    `${BASE}/${encodeURIComponent(sanctionId)}/ratings-preview`,
  );
}

export function applySanctionRatings(
  sanctionId: string,
  body: Schema["TournamentSanctionApplyRatings"],
): Promise<TournamentSanction> {
  return httpClient.post<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}/apply-ratings`,
    body,
  );
}

export function confirmSanctionFacts(
  sanctionId: string,
  body: Schema["TournamentSanctionConfirmFacts"],
): Promise<TournamentSanction> {
  return httpClient.post<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}/confirm-facts`,
    body,
  );
}

export function closeSanctionUnrated(
  sanctionId: string,
  body: Schema["TournamentSanctionCloseUnrated"],
): Promise<TournamentSanction> {
  return httpClient.post<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}/close-unrated`,
    body,
  );
}

export function revokeSanction(
  sanctionId: string,
  body: Schema["TournamentSanctionRevoke"],
): Promise<TournamentSanction> {
  return httpClient.post<TournamentSanction>(
    `${BASE}/${encodeURIComponent(sanctionId)}/revoke`,
    body,
  );
}
